import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Building2, ChevronRight, AlertCircle, Copy, Check, MapPin, Mail } from "lucide-react";
import { listClients } from "../../../api/chatbotApi";
import useAuth from "../../../hooks/useAuth";

export default function ChatbotClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedClientId, setCopiedClientId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreateClient = Number(user?.rawRole) === 0;

  const load = () => {
    setLoading(true);
    setError("");
    listClients()
      .then(setClients)
      .catch((err) => setError(err.message || "Failed to load clients"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCopyClientId = async (clientId) => {
    await navigator.clipboard.writeText(String(clientId));
    setCopiedClientId(clientId);
    setTimeout(() => setCopiedClientId(null), 1500);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">Manage top-level chatbot organizations and client accounts</p>
        </div>
        {canCreateClient ? (
          <button
            onClick={() => navigate("/clients/new-client")}
            className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0959bb] transition"
          >
            <Plus size={18} /> New Client
          </button>
        ) : null}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Client Directory */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-slate-500 text-sm">
            Loading clients...
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 mb-3">
              <Building2 size={24} />
            </div>
            <h3 className="text-base font-semibold text-slate-900">No clients registered</h3>
            <p className="text-sm text-slate-500 mt-1">No chatbot client organizations have been registered yet.</p>
            {canCreateClient ? (
              <button
                onClick={() => navigate("/clients/new-client")}
                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                <Plus size={16} /> Create Client
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="mb-5 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Client directory</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {clients.length} {clients.length === 1 ? "organization" : "organizations"}
                </p>
              </div>
              <Building2 size={20} className="text-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {clients.map((c) => (
                <article key={c.id} className="flex min-h-60 flex-col rounded-2xl border border-slate-200 bg-slate-50/50 p-5 transition hover:-translate-y-0.5 hover:border-[#096dea]/30 hover:bg-white hover:shadow-md">
                  <div className="-mx-5 -mt-5 flex items-start justify-between gap-3 rounded-t-2xl border-b border-[#d7e9ff] bg-[#eef5ff] p-5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="truncate text-base font-bold text-slate-900" title={c.name}>{c.name}</h2>
                        <button
                          type="button"
                          onClick={() => handleCopyClientId(c.id)}
                          className="shrink-0 text-slate-400 transition hover:text-[#096dea]"
                          aria-label={`Copy ID for ${c.name}`}
                          title={copiedClientId === c.id ? "Client ID copied" : "Copy client ID"}
                        >
                          {copiedClientId === c.id ? <Check size={15} /> : <Copy size={15} />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs font-medium text-slate-500">Client organization</p>
                    </div>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#096dea] shadow-sm">
                      <Building2 size={18} />
                    </div>
                  </div>

                  <div className="space-y-3 pt-5 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="shrink-0 text-slate-400" />
                      <span className="truncate">{c.address?.city || "No city added"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="shrink-0 text-slate-400" />
                      <span className="truncate">{c.contact_details?.email || "No email added"}</span>
                    </div>
                  </div>

                  <div className="mt-auto border-t border-slate-200 pt-4">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <button
                        onClick={() => navigate(`/clients/${c.id}/users`)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]"
                      >
                        Users
                      </button>
                      <button
                        onClick={() => navigate(`/clients/${c.id}/tools`)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]"
                      >
                        Tools
                      </button>
                      <button
                        onClick={() => navigate(`/clients/${c.id}`)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]"
                      >
                        Bots <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>

    </div>
  );
}
